import Vue from 'vue'
import PresentationAttributeApi from '@/api/presentation-attribute'
import constant from '@/constant'
import { resolveInkAwarePalette } from '@/utils/ink-aware-palette.util'
import sentry, { isExpectedTransientResponse } from '@/3rd-parties/sentry'

const SYSTEM_FIELDS = 'systemField'
const participantSystemField = [
  {
    name: 'name',
    type: 'name',
    fieldType: SYSTEM_FIELDS,
    label: 'Enter your name:',
    required: false,
    id: 1,
  },
  {
    name: 'email',
    type: 'email',
    fieldType: SYSTEM_FIELDS,
    label: 'Enter your email address:',
    required: false,
    id: 3,
  },
  {
    name: 'avatar',
    type: 'avatar',
    fieldType: SYSTEM_FIELDS,
    label: 'Choose an emoji as your avatar:',
    required: false,
    id: 11,
  },
]

const _state = {
  presentationAttribute: {},
  presentationAttributeLoaded: false,
}

const mutations = {
  setPresentationAttribute(state, payload) {
    state.presentationAttribute = payload
    state.presentationAttributeLoaded = true
  },
  updatePresentationAttribute(state, payload) {
    Vue.set(state.presentationAttribute, payload.attributeName, payload.attributeValue)
  },
}

const actions = {
  async getPresentationAttributeAction({ commit }, presentationId) {
    try {
      const resp = await PresentationAttributeApi.getPresentationAttributes(presentationId)
      commit('setPresentationAttribute', resp.body)
      return resp.body
    } catch (error) {
      if (!isExpectedTransientResponse(error)) {
        // Pass the raw error (not wrapped) so beforeSend's classifyEvent can see
        // a genuine vue-resource Response and relabel it into one groupable
        // HttpResponseError signal instead of an unactionable `<object>.value`.
        sentry.captureException(error, { extra: { presentationId } })
      }
      return {}
    }
  },
}

const _getters = {
  presentationColorPalette: (state, _, __, rootGetters) => {
    const savedPalette = state.presentationAttribute?.colorPalette
    if (savedPalette?.colors) {
      return savedPalette.inkAware
        ? resolveInkAwarePalette(savedPalette.colors, rootGetters?.['themeModule/activeActivityWithTheme']?.textColour)
        : savedPalette.colors
    }
    if (rootGetters?.['featureToggles/isFeatureEnabled']?.('release-ds-chart-palette')) {
      return resolveInkAwarePalette(
        constant.dsChartPalette.colors,
        rootGetters['themeModule/activeActivityWithTheme']?.textColour,
      )
    }
    return constant.colors2025
  },
  presentationColorPaletteIsInkAware: (state, _, __, rootGetters) => {
    const savedPalette = state.presentationAttribute?.colorPalette
    if (savedPalette?.colors) return savedPalette.inkAware === true
    return Boolean(rootGetters?.['featureToggles/isFeatureEnabled']?.('release-ds-chart-palette'))
  },
  presentationLighterColorPalette: (state, _, __, rootGetters) => {
    const savedPalette = state.presentationAttribute?.colorPalette
    if (savedPalette?.lighterColors) {
      return savedPalette.lighterColors
    }
    if (!savedPalette?.colors && rootGetters?.['featureToggles/isFeatureEnabled']?.('release-ds-chart-palette')) {
      return constant.dsChartPalette.lighterColors
    }
    return constant.lighterColors2025
  },

  presentationAttributeColorPalette: (state) => {
    return state.presentationAttribute.colorPalette || {}
  },
  presentationParticipantFields: (state) => {
    return [
      ...participantSystemField,
      ...(state.presentationAttribute?.participantFields?.fields || []),
    ]
  },
  // Drives the force-login gate in Presentation.vue. Backend stores this as a
  // PresentationAttribute row with attributeName = 'participantVerification' and a JSONB
  // attributeValue (extensible namespace — future fields expected: allowedDomains,
  // ssoProvider, etc.). Strict `=== true` comparison is intentional: any missing/falsy/
  // non-boolean value defaults to "off" (fail-open), mirroring how the existing attribute
  // fetch swallows errors.
  isParticipantVerificationEnabled: (state) => {
    return state.presentationAttribute?.participantVerification?.enabled === true
  },
  // The presenter persists the quiz auto-assign toggle as a presentation attribute rather
  // than a synced presentation column, so the audience must read it here to hide the team
  // picker at join. Tolerant of a bare boolean value or an { enabled } wrapper. Behind
  // AHA-43163-auto-assign-teams-default: flag on defaults an unset attribute to ON, flag off
  // (or a fetch that never succeeded, so "unset" can't be told apart from "unknown") requires
  // an explicit true, matching the fail-safe direction of isParticipantVerificationEnabled.
  isAutoAssignTeamsEnabled: (state, _, __, rootGetters) => {
    const value = state.presentationAttribute?.autoAssignTeams
    const defaultOn = state.presentationAttributeLoaded
      && rootGetters['featureToggles/canAutoAssignTeamsDefaultOn']
    if (value && typeof value === 'object') {
      return defaultOn ? value.enabled !== false : value.enabled === true
    }
    return defaultOn ? value !== false : value === true
  },
}

export default {
  namespaced: true,
  state: _state,
  mutations,
  actions,
  getters: _getters,
}
